import React, { useState, useEffect } from 'react';
import * as photosApi from '../api/photos';

/* ─── 月別集計を全ユーザー分マージ ─── */
function buildOverallMonthly(stats) {
  const map = {};
  for (const u of stats) {
    for (const m of u.monthly) {
      const key = `${m.year}-${String(m.month).padStart(2, '0')}`;
      map[key] = (map[key] ?? 0) + m.sorted_count;
    }
  }
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, count]) => {
      const [year, month] = key.split('-').map(Number);
      return { year, month, sorted_count: count };
    });
}

/* ─── 月別展開行 ─── */
function MonthlyRows({ monthly, colSpan }) {
  if (!monthly?.length) return null;
  return (
    <>
      {[...monthly].reverse().map((m, i) => (
        <tr key={i} style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
          <td colSpan={colSpan} />
          <td style={{ padding: '6px 12px 6px 32px', fontSize: 11, color: 'var(--text-muted)' }}>
            {m.year}年{String(m.month).padStart(2, '0')}月
          </td>
          <td style={{ padding: '6px 20px', fontSize: 11, textAlign: 'right', color: '#555', fontWeight: 600 }}>
            {m.sorted_count.toLocaleString()} 枚
          </td>
        </tr>
      ))}
    </>
  );
}

/* ─── 全体サマリーテーブル ─── */
function OverallSummary({ stats }) {
  const now = new Date();
  const overall = buildOverallMonthly(stats);
  const totalAll = stats.reduce((s, u) => s + u.total_sorted, 0);
  const thisMonthTotal = overall.find(
    m => m.year === now.getFullYear() && m.month === now.getMonth() + 1
  )?.sorted_count ?? 0;

  return (
    <div style={{ marginBottom: 36 }}>
      {/* KPI カード */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{
          flex: 1, minWidth: 160,
          background: 'linear-gradient(135deg, #9B5FE3, #7048D4)',
          borderRadius: 20, padding: '22px 26px', color: '#fff',
          boxShadow: '0 6px 20px rgba(112,72,212,0.25)',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.8, marginBottom: 6, letterSpacing: '0.07em' }}>
            累計振り分け枚数（全ユーザー）
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1 }}>
            {totalAll.toLocaleString()}
          </div>
          <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>枚</div>
        </div>

        <div style={{
          flex: 1, minWidth: 160,
          background: '#fff', borderRadius: 20, padding: '22px 26px',
          border: '1px solid var(--border)', boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, letterSpacing: '0.07em' }}>
            今月の振り分け枚数（合計）
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1, color: '#FF6B9D' }}>
            {thisMonthTotal.toLocaleString()}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            {now.getFullYear()}年{now.getMonth() + 1}月
          </div>
        </div>

        <div style={{
          flex: 1, minWidth: 160,
          background: '#fff', borderRadius: 20, padding: '22px 26px',
          border: '1px solid var(--border)', boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, letterSpacing: '0.07em' }}>
            利用ユーザー数
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1, color: '#06a96e' }}>
            {stats.length.toLocaleString()}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>アカウント</div>
        </div>
      </div>

      {/* 月別合計テーブル */}
      {overall.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: 16, border: '1px solid var(--border)',
          overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
        }}>
          <div style={{
            padding: '12px 20px', borderBottom: '1px solid var(--border)',
            fontWeight: 700, fontSize: 13, color: 'var(--text-muted)',
          }}>
            月別 振り分け枚数（全ユーザー合計）
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: '#fafafa', color: 'var(--text-muted)', textAlign: 'left' }}>
                <th style={{ padding: '9px 20px', fontWeight: 600 }}>年月</th>
                <th style={{ padding: '9px 20px', fontWeight: 600, textAlign: 'right' }}>合計枚数</th>
                {/* 将来: <th>売上</th> */}
              </tr>
            </thead>
            <tbody>
              {[...overall].reverse().map((m, i) => {
                const isCurrent = m.year === now.getFullYear() && m.month === now.getMonth() + 1;
                return (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-light)', background: isCurrent ? '#fdf5ff' : 'transparent' }}>
                    <td style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isCurrent && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: '#9B5FE3', color: '#fff' }}>
                          今月
                        </span>
                      )}
                      <span style={{ fontWeight: isCurrent ? 700 : 400 }}>
                        {m.year}年{String(m.month).padStart(2, '0')}月
                      </span>
                    </td>
                    <td style={{ padding: '10px 20px', textAlign: 'right', fontWeight: 700, fontSize: 15 }}>
                      {m.sorted_count.toLocaleString()}
                      <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 4 }}>枚</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── ユーザー別実績テーブル ─── */
function UserStatsTable({ stats }) {
  const [expanded, setExpanded] = useState({});

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const sorted = [...stats].sort((a, b) => b.total_sorted - a.total_sorted);

  return (
    <div style={{
      background: '#fff', borderRadius: 16, border: '1px solid var(--border)',
      overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
    }}>
      <div style={{
        padding: '12px 20px', borderBottom: '1px solid var(--border)',
        fontWeight: 700, fontSize: 13, color: 'var(--text-muted)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        ユーザー別 振り分け実績
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
          background: 'var(--border-light)', border: '1px solid var(--border)', color: 'var(--text-muted)',
        }}>
          {stats.length} 件
        </span>
      </div>

      {sorted.length === 0 ? (
        <div className="empty-state" style={{ padding: '40px 20px' }}>
          <div className="empty-icon">📊</div>
          <div className="empty-text">振り分け実績がありません</div>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: '#fafafa', color: 'var(--text-muted)', textAlign: 'left' }}>
              <th style={{ padding: '9px 20px', fontWeight: 600 }}>メールアドレス</th>
              <th style={{ padding: '9px 20px', fontWeight: 600, textAlign: 'right' }}>累計枚数</th>
              <th style={{ padding: '9px 20px', fontWeight: 600 }}>月別</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(u => (
              <React.Fragment key={u.user_id}>
                <tr style={{ borderBottom: expanded[u.user_id] ? 'none' : '1px solid var(--border-light)' }}>
                  <td style={{ padding: '11px 20px', fontWeight: 500 }}>{u.email}</td>
                  <td style={{ padding: '11px 20px', textAlign: 'right', fontWeight: 700, fontSize: 15 }}>
                    {u.total_sorted.toLocaleString()}
                    <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 4 }}>枚</span>
                  </td>
                  <td style={{ padding: '11px 20px' }}>
                    {u.monthly.length > 0 ? (
                      <button
                        onClick={() => toggle(u.user_id)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--primary)', fontSize: 11, padding: 0, textDecoration: 'underline',
                        }}
                      >
                        {expanded[u.user_id] ? '▲ 閉じる' : `▼ ${u.monthly.length}ヶ月分`}
                      </button>
                    ) : (
                      <span style={{ fontSize: 11, color: '#aaa' }}>—</span>
                    )}
                  </td>
                </tr>
                {expanded[u.user_id] && (
                  <MonthlyRows monthly={u.monthly} colSpan={1} />
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ─── メインページ ─── */
export default function AdminUsagePage() {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    photosApi.getAdminStats()
      .then(setStats)
      .catch(() => setError('利用実績データの取得に失敗しました'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="page">
      <div className="page-header animate-in">
        <h1 className="page-title">利用実績・分析</h1>
        <p className="page-subtitle">ユーザー別の振り分け実績と全体サマリーを確認できます</p>
      </div>

      <div className="page-body">
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80 }}>
            <span className="spinner spinner-dark" style={{ width: 28, height: 28 }} />
          </div>
        ) : error ? (
          <div className="msg msg-error">{error}</div>
        ) : (
          <div className="animate-in">
            <OverallSummary stats={stats} />
            <UserStatsTable stats={stats} />
            {/* 将来追加予定: サブスクリプション状況、売上管理 */}
          </div>
        )}
      </div>
    </div>
  );
}
